import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
}

const ParticleCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animFrameRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    particlesRef.current = Array.from({ length: 55 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      size: Math.random() * 1.5 + 0.4,
      speedX: (Math.random() - 0.5) * 0.25,
      speedY: (Math.random() - 0.5) * 0.25,
      opacity: Math.random() * 0.3 + 0.08,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,169,110,${p.opacity})`;
        ctx.fill();
      });

      particlesRef.current.forEach((p, i) => {
        particlesRef.current.slice(i + 1).forEach((q) => {
          const dist = Math.hypot(p.x - q.x, p.y - q.y);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(201,169,110,${0.06 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.4;
            ctx.stroke();
          }
        });
      });
      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.7 }}
    />
  );
};

const HeroSection = () => {
  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
      style={{ background: 'radial-gradient(ellipse at 50% 40%, #0F172A 0%, #0B0B0D 65%)' }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/6cb75fafc7e23f70866ba8cc820927b0.jpg"
          alt="LOPES BUSINESS"
          className="w-full h-full object-cover object-top opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0D]/50 via-transparent to-[#0B0B0D]/90"></div>
      </div>

      {/* Subtle center glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(30,58,138,0.12) 0%, transparent 70%)' }}
      />

      <ParticleCanvas />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full">

        {/* Main Title */}
        <h1
          className="font-serif text-5xl md:text-6xl lg:text-[4.5rem] font-light text-white leading-[1.15] mb-7"
        >
          Transformamos carreiras{' '}
          <br className="hidden md:block" />
          <em className="not-italic text-[#C9A96E]">esportivas e patrimônios</em>
          <br className="hidden md:block" />
          em estruturas sólidas e globais.
        </h1>

        {/* Divider */}
        <div className="w-12 h-px bg-[#C9A96E]/50 mx-auto mb-7" />

        {/* Subtitle */}
        <p className="font-sans text-base md:text-lg font-light text-white/55 max-w-2xl mx-auto leading-relaxed mb-10">
          Consultoria estratégica, blindagem patrimonial e operações financeiras
          internacionais para atletas e empresários de alta performance.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://wa.me/5547997236779"
            target="_blank"
            rel="nofollow noreferrer"
            className="font-sans text-sm font-semibold tracking-[0.2em] uppercase bg-[#C9A96E] text-[#0B0B0D] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap hover:opacity-90"
            style={{ boxShadow: '0 0 28px rgba(201,169,110,0.28)' }}
          >
            Fale com um Especialista
          </a>
          <a
            href="/servicos"
            className="font-sans text-sm font-medium tracking-[0.2em] uppercase border border-white/20 text-white/70 px-10 py-4 rounded-full hover:border-[#C9A96E]/40 hover:text-[#C9A96E] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Nossos Serviços
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <div className="w-px h-10 bg-gradient-to-b from-transparent to-[#C9A96E]" />
        <i className="ri-arrow-down-line text-[#C9A96E] text-xs" />
      </div>
    </section>
  );
};

export default HeroSection;
